import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { format, addDays, startOfWeek } from "date-fns";
import { ru } from "date-fns/locale";
import type { ServiceCenterPostResponseDto } from "@pro-sto/api-client";
import { NewBookingButton } from "@/components/NewBookingButton";
import PostFilter from "../PostFilter";
import "./CalendarHeader.css";

interface CalendarHeaderProps {
  currentDate: Date;
  onDateChange: (date: Date) => void;
  viewMode: "day" | "week";
  onViewModeChange: (mode: "day" | "week") => void;
  onAddBooking: () => void;
  pendingBookingsCount?: number;
  onPendingBookingsClick?: () => void;
  posts?: ServiceCenterPostResponseDto[];
  selectedPostUuids?: string[];
  onSelectedPostsChange?: (postUuids: string[]) => void;
}

const ClockIcon: React.FC = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 28 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="14"
      cy="14"
      r="10.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14 7V14L18.2 18.2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PlusIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 4.16669V15.8334M4.16669 10H15.8334"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronLeftIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.5 15L7.5 10L12.5 5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRightIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.5 15L12.5 10L7.5 5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  currentDate,
  onDateChange,
  viewMode,
  onViewModeChange,
  onAddBooking,
  pendingBookingsCount = 0,
  onPendingBookingsClick,
  posts = [],
  selectedPostUuids = [],
  onSelectedPostsChange,
}: CalendarHeaderProps) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    // Часы показываются только в режиме 'день' — в режиме 'неделя' не тикаем впустую,
    // иначе вся шапка (вместе с PostFilter и баннером заявок) ре-рендерится каждую секунду
    if (viewMode !== "day") return;

    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, [viewMode]);

  const handlePrevious = () => {
    const newDate = dayjs(currentDate)
      .subtract(viewMode === "week" ? 7 : 1, "day")
      .toDate();
    onDateChange(newDate);
  };

  const handleNext = () => {
    const newDate = dayjs(currentDate)
      .add(viewMode === "week" ? 7 : 1, "day")
      .toDate();
    onDateChange(newDate);
  };

  const handleToday = () => {
    onDateChange(new Date());
  };

  const formattedTime = dayjs(currentTime).format("HH:mm:ss");

  // Диапазон недели для отображения в навигации
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = addDays(weekStart, 6);

  const weekRangeLabel = (() => {
    const isSameMonth =
      weekStart.getMonth() === weekEnd.getMonth() &&
      weekStart.getFullYear() === weekEnd.getFullYear();
    if (isSameMonth) {
      return `${format(weekStart, "dd", { locale: ru })}-${format(weekEnd, "dd MMMM", { locale: ru })}`;
    }
    return `${format(weekStart, "dd MMM", { locale: ru })} - ${format(weekEnd, "dd MMM", { locale: ru })}`;
  })();

  return (
    <>
      <div className="calendar-header" data-testid="calendar-header">
        <div className="calendar-header__left">
          {pendingBookingsCount > 0 && onPendingBookingsClick && (
            <NewBookingButton
              count={pendingBookingsCount}
              onClick={onPendingBookingsClick}
            />
          )}
          <button
            className="calendar-header__add"
            onClick={onAddBooking}
            type="button"
            aria-label="Добавить запись"
            data-testid="add-booking-button"
          >
            <span className="calendar-header__add-text">Добавить запись</span>
            <span className="calendar-header__add-icon" aria-hidden="true">
              <PlusIcon />
            </span>
          </button>
          {viewMode === "week" && onSelectedPostsChange && (
            <PostFilter
              posts={posts}
              selectedPostUuids={selectedPostUuids}
              onChange={onSelectedPostsChange}
            />
          )}
          {viewMode === "day" && (
            <div className="calendar-header__time" data-testid="calendar-time">
              <ClockIcon />
              <span className="calendar-header__time-text">{formattedTime}</span>
            </div>
          )}
        </div>

        <div className="calendar-header__right">
          <button
            className="calendar-header__today"
            onClick={handleToday}
            type="button"
            data-testid="calendar-today-button"
          >
            Сегодня
          </button>

          <div
            className="calendar-header__tabs"
            aria-label="Режим отображения"
            data-testid="calendar-view-selector"
          >
            <button
              className={`calendar-header__tab ${viewMode === "day" ? "calendar-header__tab_active" : ""}`}
              onClick={() => onViewModeChange("day")}
              type="button"
              data-testid="calendar-view-day"
            >
              День
            </button>
            <button
              className={`calendar-header__tab ${viewMode === "week" ? "calendar-header__tab_active" : ""}`}
              onClick={() => onViewModeChange("week")}
              type="button"
              data-testid="calendar-view-week"
            >
              Неделя
            </button>
          </div>

          <div
            className="calendar-header__navigation"
            aria-label={
              viewMode === "day" ? "Переключение дня" : "Переключение недели"
            }
          >
            <button
              className="calendar-header__nav-button"
              onClick={handlePrevious}
              aria-label={
                viewMode === "day" ? "Предыдущий день" : "Предыдущая неделя"
              }
              type="button"
              data-testid="calendar-prev-button"
            >
              <ChevronLeftIcon />
            </button>
            <span
              className="calendar-header__week-range"
              aria-label={
                viewMode === "day" ? "Текущий день" : "Текущая неделя"
              }
              data-testid="calendar-date-display"
            >
              {viewMode === "day"
                ? format(currentDate, "d MMMM", { locale: ru })
                : weekRangeLabel}
            </span>
            <button
              className="calendar-header__nav-button"
              onClick={handleNext}
              aria-label={
                viewMode === "day" ? "Следующий день" : "Следующая неделя"
              }
              type="button"
              data-testid="calendar-next-button"
            >
              <ChevronRightIcon />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CalendarHeader;
